import React, { useMemo } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

const getSelectTheme = (isDark) => (theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary: isDark ? "#6b7fd7" : "#24695c",
		primary25: isDark ? "#373737" : "#e6f7ff",
		primary50: isDark ? "#4a5464" : "#d6e8ff",
		neutral0: isDark ? "#262932" : "#ffffff",
		neutral20: isDark ? "#4a5464" : "#d0d5dd",
		neutral30: isDark ? "#5c6678" : "#cbd5e1",
		neutral40: isDark ? "#8b95a8" : "#98a2b3",
		neutral50: isDark ? "#a8b0be" : "#98a2b3",
		neutral80: isDark ? "#e8eaef" : "#344054",
	},
});

const getSelectStyles = (isDark) => ({
	control: (base, state) => ({
		...base,
		minHeight: 36,
		fontSize: "0.875rem",
		borderRadius: 8,
		borderColor: state.isFocused
			? isDark
				? "#6b7fd7"
				: "#24695c"
			: isDark
				? "#4a5464"
				: "#d0d5dd",
		boxShadow: state.isFocused
			? isDark
				? "0 0 0 3px rgba(107, 127, 215, 0.2)"
				: "0 0 0 3px rgba(36, 105, 92, 0.12)"
			: "none",
		"&:hover": {
			borderColor: isDark ? "#6b7fd7" : "#24695c",
		},
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 2000,
	}),
	option: (base) => ({
		...base,
		fontSize: "0.875rem",
		padding: "8px 12px",
	}),
});

/**
 * Reusable state select — options come from masterStates after country is chosen.
 */
const StateSelect = ({
	options = [],
	value = null,
	onChange,
	placeholder,
	isDisabled = false,
	isLoading = false,
	inModal = true,
	className = "",
}) => {
	const { t } = useTranslation();
	const isDark = document.body.classList.contains("dark-only");
	const styles = useMemo(() => getSelectStyles(isDark), [isDark]);

	return (
		<Select
			className={className}
			classNamePrefix="state-select"
			options={options}
			value={value}
			onChange={(option) => onChange?.(option || null)}
			isClearable
			isSearchable
			isDisabled={isDisabled}
			isLoading={isLoading}
			placeholder={placeholder || t("Select state")}
			noOptionsMessage={() =>
				isLoading ? t("Loading...") : t("No states found")
			}
			theme={getSelectTheme(isDark)}
			styles={styles}
			menuPortalTarget={
				inModal && typeof document !== "undefined" ? document.body : null
			}
			menuPosition={inModal ? "fixed" : "absolute"}
			menuPlacement="auto"
			menuShouldScrollIntoView={false}
		/>
	);
};

export default StateSelect;
